import { Container, Graphics, Rectangle, Sprite } from 'pixi.js';
import { getTexture } from '@/assets/resolver';
import { COLORS } from '@/config/colors';
import { PLAYER } from '@/config/player';
import { t } from '@/i18n';
import { cellGlyph, shieldGlyph } from './shop-actions';
import {
  CHIPS_C, MAIN_H, MAX_CHIPS_C, NEXT_Y, STATS_C, VITAL_TILE, chipCellPos,
} from './shop-compact-layout';
import type { BayHooks } from './shop-compose';
import { buildCollectionChip } from './shop-tooltips';
import type { FocusItem, ShopState } from './shop-types';
import { buildStatsPanel } from './stats-panel';
import { chamferPanel, label, targetBrackets } from './widgets';

/*
 * Phone STATUS page (polish 1): the two vital buys (repair, armor) as big
 * tiles, the next room, the owned-card chips and the SHIP STATUS sheet
 * (focusing a chip lights the rows it touches).
 */

const GLYPH_SCALE = 1.5;
/** A chip's hex is ~42 px tall — its tap box is the full grid cell */
const CHIP_HIT = new Rectangle(-CHIPS_C.pitchX / 2 + 1, -CHIPS_C.pitchY / 2, CHIPS_C.pitchX - 2, CHIPS_C.pitchY);
const GAIN_SIZE = 18;
const COL_W = VITAL_TILE.w;

/** Big wordless vital buy: glyph, "+N", gem price (or a free ×N stack). */
function vitalTile(
  selected: boolean, enabled: boolean, glyph: (g: Graphics) => void, gain: string, accent: number,
  price: { value: number; red: boolean } | { free: number },
): Container {
  const { w, h } = VITAL_TILE;
  const c = new Container();
  c.addChild(chamferPanel(new Graphics(), w, h, {
    fill: selected ? 0x2a2440 : 0x1a1626,
    stroke: selected ? COLORS.uiAccent : enabled ? accent : 0x3a3548, strokeWidth: selected ? 3 : 2, notch: 12,
  }));
  const icon = new Graphics();
  glyph(icon);
  icon.scale.set(GLYPH_SCALE);
  icon.position.set(16, h / 2);
  icon.alpha = enabled ? 1 : 0.45;
  const txt = label(gain, GAIN_SIZE, enabled ? 0xffffff : 0x5a6168, 1);
  txt.anchor.set(0, 0.5);
  txt.position.set(58, h / 2);
  c.addChild(icon, txt);
  const x = txt.x + txt.width + 16;
  if ('free' in price) {
    const stack = label(`×${price.free}`, GAIN_SIZE, COLORS.hudArmor, 1);
    stack.anchor.set(0, 0.5);
    stack.position.set(x + 8, h / 2);
    const box = new Graphics();
    box.roundRect(x, h / 2 - 15, stack.width + 16, 30, 8).fill({ color: COLORS.hudArmor, alpha: 0.18 })
      .stroke({ color: COLORS.hudArmor, width: 1.5 });
    c.addChild(box, stack);
  } else {
    const gem = new Sprite(getTexture(price.red ? 'pickup/gem-red' : 'pickup/gem-yellow'));
    gem.anchor.set(0, 0.5);
    gem.scale.set(0.55);
    gem.position.set(x, h / 2);
    const n = label(String(price.value), GAIN_SIZE, price.red ? COLORS.gemRed : COLORS.gemYellow, 1);
    n.anchor.set(0, 0.5);
    n.position.set(x + gem.width + 4, h / 2);
    if (!enabled) gem.alpha = n.alpha = 0.45;
    c.addChild(gem, n);
  }
  if (selected) c.addChild(targetBrackets(new Graphics(), w, h, COLORS.uiAccent, 12, 4));
  return c;
}

/** Next room: label, arena name, boss silhouette + name + level / waves. */
function nextRoom(s: ShopState): Container {
  const c = new Container();
  const head = label(t('shop.next'), 13, 0x9aa0a6, 2);
  head.position.set(2, NEXT_Y);
  const arena = label(s.arenaName, 14, COLORS.playerCyan, 1);
  arena.position.set(2, NEXT_Y + 18);
  if (arena.width > COL_W) arena.scale.set(COL_W / arena.width);
  c.addChild(head, arena);
  if (!s.next) return c;
  const sil = new Sprite(getTexture(s.next.bodyKey));
  sil.anchor.set(0.5);
  sil.tint = 0x141022;
  sil.scale.set(40 / Math.max(sil.texture.width, sil.texture.height));
  sil.position.set(22, NEXT_Y + 60);
  const name = label(s.next.name, 14, 0xd5d9de, 1);
  name.anchor.set(0, 0.5);
  name.position.set(48, NEXT_Y + 52);
  if (name.width > COL_W - 50) name.scale.set((COL_W - 50) / name.width);
  const parts = [t('shop.level', { n: s.next.level })];
  if (s.next.waves > 0) parts.push(t('shop.nextWaves', { n: s.next.waves }));
  const info = label(parts.join(' · '), 13, 0x9aa0a6, 0);
  info.anchor.set(0, 0.5);
  info.position.set(48, NEXT_Y + 70);
  if (info.width > COL_W - 50) info.scale.set((COL_W - 50) / info.width);
  c.addChild(sil, name, info);
  return c;
}

/** The whole STATUS page, page-local (the caller places it at MAIN_Y). */
export function buildCompactStatus(s: ShopState, sel: FocusItem | undefined, hooks: BayHooks): Container {
  const c = new Container();
  const player = s.ctx.player;
  const heal = vitalTile(
    hooks.isSel({ kind: 'heal' }), s.red >= s.healPrice && player.hp < player.maxHp,
    (g) => cellGlyph(g, -10, 0, COLORS.hudHp), `+${PLAYER.cellHp}`, 0x2e8c80,
    { value: s.healPrice, red: true },
  );
  hooks.interactive(heal, { kind: 'heal' });
  const stored = s.armorItems > 0;
  const armor = vitalTile(
    hooks.isSel({ kind: 'buyArmor' }), !s.armorFull && (stored || s.yellow >= s.armorPrice),
    (g) => shieldGlyph(g, -8, 0, COLORS.hudArmor), '+1', 0x8c7a2e,
    stored ? { free: s.armorItems } : { value: s.armorPrice, red: false },
  );
  armor.position.set(0, VITAL_TILE.h + VITAL_TILE.gap);
  hooks.interactive(armor, { kind: 'buyArmor' });
  c.addChild(heal, armor, nextRoom(s));

  // Owned cards (inspect: lights their rows in the sheet)
  const owned = s.collection.reduce((sum, e) => sum + e.count, 0);
  const title = label(`${t('shop.collectionTitle')} — ${owned}`, 13, COLORS.uiAccent, 1);
  title.position.set(CHIPS_C.x, 0);
  const chipsW = CHIPS_C.cols * CHIPS_C.pitchX;
  if (title.width > chipsW) title.scale.set(chipsW / title.width);
  c.addChild(title);
  s.collection.slice(0, MAX_CHIPS_C).forEach((e, i) => {
    const item: FocusItem = { kind: 'chip', cardId: e.card.id };
    const chip = buildCollectionChip(e.card, e.count, hooks.isSel(item));
    const p = chipCellPos(i);
    chip.position.set(p.x, p.y);
    chip.hitArea = CHIP_HIT;
    hooks.interactive(chip, item);
    c.addChild(chip);
  });
  if (s.collection.length > MAX_CHIPS_C) {
    const more = label(`+${s.collection.length - MAX_CHIPS_C}`, 14, 0x9aa0a6, 0);
    more.anchor.set(1, 1);
    more.position.set(CHIPS_C.x + chipsW, MAIN_H);
    c.addChild(more);
  }

  const focused = sel?.kind === 'chip' ? s.collection.find((e) => e.card.id === sel.cardId)?.card ?? null : null;
  const sheet = buildStatsPanel(STATS_C.w, MAIN_H, s.ctx, focused, focused !== null);
  sheet.position.set(STATS_C.x, 0);
  c.addChild(sheet);
  return c;
}
